// Slice RTK per i singleton primitivi (1 chiave di stato, 1 setter "set").
//
// Per evitare 15+ file boilerplate, raccolgo qui tutte le slice singleton
// definite tramite l'helper `makeSingletonSlice`. Ogni slice gestisce una
// chiave di stato al top level, per preservare la legacy shape (vedi
// `cdiFiltersSlice.js` per la doc del pattern).

import { createSlice } from '@reduxjs/toolkit';

/**
 * Crea uno slice "singleton": una chiave di stato con un solo reducer `set`
 * che la sostituisce per intero. Le slice piu' complesse (entity adapter,
 * stream listener, merge logic) NON usano questo helper.
 */
function makeSingletonSlice(name, initialState) {
  const slice = createSlice({
    name,
    initialState,
    reducers: {
      set: (_state, action) => (action.payload === undefined ? initialState : action.payload),
    },
  });
  return { reducer: slice.reducer, set: slice.actions.set };
}

const currentCheck = makeSingletonSlice('current_check', {});
const company = makeSingletonSlice('company', '');
const role = makeSingletonSlice('role', '');
const trip = makeSingletonSlice('trip', {});
const dailyTrip = makeSingletonSlice('daily_trip', {});
const datesHistory = makeSingletonSlice('dates_history', null);
const days = makeSingletonSlice('days', [0, 1, 2, 3, 4, 5, 6]);
const tripsOfTheDay = makeSingletonSlice('trips_of_the_day', []);
const pendings = makeSingletonSlice('pendings', []);
const user = makeSingletonSlice('user', null);
const selectedGestore = makeSingletonSlice('selected_gestore', null);
const selectedCompany = makeSingletonSlice('selected_company', null);
const selectedAutista = makeSingletonSlice('selected_autista', null);
const groupTripData = makeSingletonSlice('group_trip_data', null);
const selectedPaymentStatus = makeSingletonSlice('selected_payment_status', null);
const selectedInvoiceStatus = makeSingletonSlice('selected_invoice_status', null);
const selectedPaymentType = makeSingletonSlice('selected_payment_type', null);
const historyPrivate = makeSingletonSlice('history_private', false);
const currentManager = makeSingletonSlice('currentManager', null);
const vehicleTypes = makeSingletonSlice('vehicle_types', []);
const equipmentTypes = makeSingletonSlice('equipment_types', []);
const processTypes = makeSingletonSlice('process_types', []);
const positions = makeSingletonSlice('positions', {});

// --- Reducers ---------------------------------------------------------------
// Map { stateKey: reducer } che il rootReducer aggancia alla sliceReducers.
export const singletonReducers = {
  current_check: currentCheck.reducer,
  company: company.reducer,
  role: role.reducer,
  trip: trip.reducer,
  daily_trip: dailyTrip.reducer,
  dates_history: datesHistory.reducer,
  days: days.reducer,
  trips_of_the_day: tripsOfTheDay.reducer,
  pendings: pendings.reducer,
  user: user.reducer,
  selected_gestore: selectedGestore.reducer,
  selected_company: selectedCompany.reducer,
  selected_autista: selectedAutista.reducer,
  group_trip_data: groupTripData.reducer,
  selected_payment_status: selectedPaymentStatus.reducer,
  selected_invoice_status: selectedInvoiceStatus.reducer,
  selected_payment_type: selectedPaymentType.reducer,
  history_private: historyPrivate.reducer,
  currentManager: currentManager.reducer,
  vehicle_types: vehicleTypes.reducer,
  equipment_types: equipmentTypes.reducer,
  process_types: processTypes.reducer,
  positions: positions.reducer,
};

// --- Setters esposti (nomi back-compat con il vecchio actions/index.js) -----
export const setCurrentCheck = currentCheck.set;
export const setCompany = company.set;
export const setUserRole = role.set;
export const setTripData = trip.set;
export const setDailyTripData = dailyTrip.set;
export const setDatesHistory = datesHistory.set;
export const setDays = days.set;
export const setTripsOfTheDay = tripsOfTheDay.set;
export const setPendings = pendings.set;
export const setUser = user.set;
export const setSelectedGestore = selectedGestore.set;
export const setSelectedCompany = selectedCompany.set;
export const setSelectedAutista = selectedAutista.set;
export const setGroupTripData = groupTripData.set;
export const setSelectedPaymentStatus = selectedPaymentStatus.set;
export const setSelectedInvoiceStatus = selectedInvoiceStatus.set;
export const setSelectedPaymentType = selectedPaymentType.set;
export const setHistoryPrivate = historyPrivate.set;
export const setCurrentManager = currentManager.set;
export const setVehicleTypes = vehicleTypes.set;
export const setEquipmentTypes = equipmentTypes.set;
export const setProcessTypes = processTypes.set;
export const setPositions = positions.set;
